import React from "react";
import { Card, CardContent } from "../ui/card";

/**
 * BehavioralActionCard — P1 SLICE 3.
 *
 * Local to the "Actions" (Behavioral LPE) reporting surface, deliberately
 * NOT a KPICard variant and NOT a KPICard prop-contract change. KPICard's
 * confidence/availability/change/benchmark axes don't apply to these
 * metrics (no such concepts are defined for Behavioral action counts), so
 * widening that shared, already-heavily-reused component for a single new
 * surface would be the wrong direction — this composes the same visual
 * primitives (Card/CardContent, the same title/value typography KPICard
 * uses) locally instead.
 *
 * `value` is the PRIMARY number — the raw canonical EVENT count (frozen
 * `grain: "event_count"`) — rendered with the same large/bold treatment
 * every other Dashboard KPI number uses. `attemptsWithAction`/
 * `journeysWithAction` are SECONDARY counts, rendered smaller and lower-
 * contrast, explicitly labeled ("Attempts with action" / "Journeys with
 * action") so a `0` there reads as "no usable quote-attempt/journey
 * identity was counted for this action" — never as "no such attempt/
 * journey existed."
 */
export default function BehavioralActionCard({ title, value, attemptsWithAction, journeysWithAction, icon: Icon }) {
  const testId = `behavioral-action-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  const displayValue = typeof value === "number" ? value.toLocaleString() : "—";
  const displayAttempts = typeof attemptsWithAction === "number" ? attemptsWithAction.toLocaleString() : "—";
  const displayJourneys = typeof journeysWithAction === "number" ? journeysWithAction.toLocaleString() : "—";

  return (
    <Card className="glass border-white/10" data-testid={testId}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs font-medium text-slate-300/70 uppercase tracking-wide">{title}</p>
          {Icon && (
            <div className="rounded-lg bg-indigo-500/20 p-1.5 shrink-0">
              <Icon className="h-4 w-4 text-indigo-300" />
            </div>
          )}
        </div>
        <p className="text-2xl font-bold text-slate-100" data-testid={`${testId}-value`}>
          {displayValue}
        </p>
        <div className="text-xs text-slate-400 space-y-0.5">
          <p data-testid={`${testId}-attempts`}>Attempts with action: {displayAttempts}</p>
          <p data-testid={`${testId}-journeys`}>Journeys with action: {displayJourneys}</p>
        </div>
      </CardContent>
    </Card>
  );
}
